/**
 * useViewport — the two width hooks Draft Plan lays itself out with.
 *
 * They lived inside DraftPlan.jsx while it was the only file that needed them.
 * PlanSummary needs the phone breakpoint too, and a component importing a hook
 * out of the shell that renders it is a cycle, so they moved here. The bodies
 * are unchanged.
 *
 * 🔴 THE innerWidth SEED IS NOT A FALLBACK, it is the whole story in jsdom.
 * jsdom ships no matchMedia, so the effect returns early and the hook keeps
 * whatever the initial read said — which is what lets a test set
 * window.innerWidth before mounting and get a deterministic layout with no
 * media-query stub at all.
 */

import { useEffect, useState } from "react";

/**
 * Below this the four-column matrix, the five-tile summary row and the toolbar
 * all stop fitting a phone. One number for all three so the page changes shape
 * once rather than three times on the way down.
 */
export const PHONE_BREAKPOINT = 640;

export function useIsNarrow(px) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth < px
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${px - 1}px)`);
    const onChange = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [px]);
  return narrow;
}

/** The mirror of useIsNarrow: true once the viewport reaches `px`. */
export function useIsWide(px) {
  const [wide, setWide] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= px
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(min-width: ${px}px)`);
    const onChange = (e) => setWide(e.matches);
    setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [px]);
  return wide;
}
